import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { removePracticeFromOrganization } from 'api/Practice';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import { show as showIntercom } from 'components/shared/IntercomButton/lib/positionMessenger';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

import InnacurateData from '../../InaccurateData/InaccurateData';

const OrganizationDetails = ({ isAdmin, practice, organization, members }) => {
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const navigate = useNavigate();

  const { mutate: handleRemove } = useCustomMutation({
    mutationFn: () =>
      removePracticeFromOrganization(navigate, {
        practice_id: practice.id,
        user_id: practice.organization.user_id
      }),
    invalidateQueryKey: ['practice'],
    successMessage: 'Practice removed from organization successfully!'
  });

  if ((!organization || !practice?.organization) && !isAdmin) {
    return (
      <div className="flex w-full flex-col items-center justify-center px-6 py-12 text-center">
        <Icon icon="no-patient" />
        <h3 className="mt-4 text-lg font-600 text-neutral-800">No organization assigned</h3>
        <p className="mt-2 max-w-[520px] text-sm font-400 text-neutral-600">
          You are not currently assigned to an organization. If your practice is part of a group or
          network, our staff will link you automatically – no action needed. To add a location,
          contact support; each must have its own MID for compliance. If this seems incorrect, reach
          support.
        </p>
        <Button
          text="Contact support"
          icon="call-calling-mono"
          className="mt-6"
          variant="link"
          onClick={showIntercom}
        />
      </div>
    );
  }

  const practiceOwner = members?.find((member) => member.role === 'Practice Owner');
  const organizationAddress = organization?.practiceAddress;
  const fullAddress = organizationAddress
    ? `${organizationAddress?.address_ln_1 || ''}${organizationAddress?.city ? ', ' + organizationAddress.city : ''}${organizationAddress?.state ? ', ' + organizationAddress.state : ''} ${organizationAddress?.zip || ''}`
    : '';

  return (
    <>
      <div className="flex w-full flex-col">
        <div className="flex flex-col gap-6 rounded-md border border-solid border-neutral-200 p-5">
          <p className="text-sm font-400 text-neutral-600">You are part of this organization:</p>
          <div className="flex items-start justify-between">
            <div className="flex gap-3">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-solid border-neutral-200 bg-white p-2">
                <Icon icon="practice-mono" size={32} color="neutral" />
              </div>
              <div className="flex flex-col">
                <h6 className="text-base font-600 text-neutral-800">
                  {practice?.organization?.name || organization?.name}
                </h6>
                <p className="text-sm font-400 text-neutral-600">{fullAddress || '-'}</p>
              </div>
            </div>
          </div>

          <div className="flex items-end justify-between gap-3 sm:flex-col sm:items-start">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-neutral-600">ID</label>
              <p className="text-sm font-500 text-neutral-800">
                {practice?.organization?.id || organization?.id}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-neutral-600">Owner</label>
              <p className="text-sm font-500 text-neutral-800">{practiceOwner?.fullName || '-'}</p>
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-sm text-neutral-600">Phone number</label>
              <p className="text-sm font-500 text-neutral-800">{organization?.phone || '-'}</p>
            </div>
            {isAdmin && (
              <Button
                style="outlined"
                text="Leave organization"
                icon="minus-circle-mono"
                iconColor="neutral-700"
                className="ml-auto border-neutral-200 bg-transparent text-neutral-900 hover:border-neutral-300 hover:bg-neutral-50"
                size="small"
                onClick={() => setConfirmationModalVisible(true)}
              />
            )}
          </div>
        </div>

        {!isAdmin && (
          <div className="mt-5">
            <InnacurateData />
          </div>
        )}
      </div>

      {confirmationModalVisible && (
        <Confirm
          variant="danger"
          title="Remove from organization?"
          icon="info-circle-filled"
          message="If you remove organization, in order to continue with setup you must create a new one or join an existing organization."
          handleOpen={confirmationModalVisible}
          handleClose={() => setConfirmationModalVisible(false)}
          footerActions={{
            primary: {
              text: 'Leave organization',
              onClick: handleRemove,
              color: 'danger'
            },
            secondary: {
              text: 'Cancel',
              onClick: () => setConfirmationModalVisible(false)
            }
          }}
        />
      )}
    </>
  );
};

export default OrganizationDetails;
